import { useState, useEffect } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { THEMES, DEFAULT_THEME, applyTheme } from '../utils/themes';
import './Settings.scss';

export default function Settings({ onClose }) {
  const { user } = useAuth();
  const [profile, setProfile]         = useState({});
  const [form, setForm]               = useState({ height: '', dob: '', sex: '', goalWeight: '' });
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [activeTheme, setActiveTheme] = useState(localStorage.getItem('rkindu-theme') || DEFAULT_THEME);
  const [tab, setTab]                 = useState('profile');

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'health', 'profiles'), snap => {
      const data = snap.exists() ? snap.data() : {};
      const p = data[user.email] || {};
      setProfile(p);
      setForm({
        height:     p.height?.toString()     || '',
        dob:        p.dob                    || '',
        sex:        p.sex                    || '',
        goalWeight: p.goalWeight?.toString() || '',
      });
      if (p.theme) {
        setActiveTheme(p.theme);
        applyTheme(p.theme);
      }
    });
    return unsub;
  }, [user.email]);

  const saveProfile = async () => {
    const h = parseFloat(form.height);
    if (!h || !form.dob || !form.sex) return;
    setSaving(true);
    const payload = {
      ...profile,
      height: h,
      dob:    form.dob,
      sex:    form.sex,
      ...(form.goalWeight ? { goalWeight: parseFloat(form.goalWeight) } : { goalWeight: null }),
    };
    await setDoc(doc(db, 'health', 'profiles'), { [user.email]: payload }, { merge: true });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleTheme = async (themeId) => {
    setActiveTheme(themeId);
    applyTheme(themeId);
    await setDoc(
      doc(db, 'health', 'profiles'),
      { [user.email]: { ...profile, theme: themeId } },
      { merge: true },
    );
  };

  const canSave = form.height && form.dob && form.sex;

  return (
    <>
      <div className="settings-overlay" onClick={onClose} />
      <div className="settings-drawer">
        <div className="settings-header">
          <h2 className="settings-title">Settings</h2>
          <button className="settings-close" onClick={onClose}>×</button>
        </div>

        <div className="settings-tabs">
          <button
            className={`settings-tab ${tab === 'profile' ? 'active' : ''}`}
            onClick={() => setTab('profile')}
          >
            👤 Profile
          </button>
          <button
            className={`settings-tab ${tab === 'theme' ? 'active' : ''}`}
            onClick={() => setTab('theme')}
          >
            🎨 Theme
          </button>
        </div>

        <div className="settings-body">
          {tab === 'profile' && (
            <div className="settings-section">
              <p className="settings-hint">
                Used for BMI &amp; body fat % calculations in the Health tab.
              </p>

              <label className="settings-label">
                Height (cm) *
                <input
                  className="settings-input"
                  type="number"
                  placeholder="e.g. 170"
                  min={50}
                  max={300}
                  value={form.height}
                  onChange={e => setForm(f => ({ ...f, height: e.target.value }))}
                />
              </label>

              <label className="settings-label">
                Date of birth *
                <input
                  className="settings-input"
                  type="date"
                  value={form.dob}
                  onChange={e => setForm(f => ({ ...f, dob: e.target.value }))}
                />
              </label>

              <div className="settings-label">
                Biological sex *
                <div className="settings-sex-toggle">
                  {['M', 'F'].map(s => (
                    <button
                      key={s}
                      type="button"
                      className={`settings-sex-btn ${form.sex === s ? 'active' : ''}`}
                      onClick={() => setForm(f => ({ ...f, sex: s }))}
                    >
                      {s === 'M' ? '♂ Male' : '♀ Female'}
                    </button>
                  ))}
                </div>
              </div>

              <label className="settings-label">
                Goal weight (kg)
                <input
                  className="settings-input"
                  type="number"
                  placeholder="e.g. 65 (optional)"
                  min={20}
                  max={500}
                  value={form.goalWeight}
                  onChange={e => setForm(f => ({ ...f, goalWeight: e.target.value }))}
                />
              </label>

              <button
                className="btn-settings-save"
                onClick={saveProfile}
                disabled={saving || !canSave}
              >
                {saved ? '✓ Saved!' : saving ? 'Saving…' : 'Save profile'}
              </button>
            </div>
          )}

          {tab === 'theme' && (
            <div className="settings-section">
              <p className="settings-hint">
                Pick a colour theme. Changes apply instantly.
              </p>
              <div className="theme-grid">
                {Object.values(THEMES).map(theme => (
                  <button
                    key={theme.id}
                    className={`theme-card ${activeTheme === theme.id ? 'active' : ''}`}
                    onClick={() => handleTheme(theme.id)}
                  >
                    <div
                      className="theme-swatch"
                      style={{
                        background: `linear-gradient(135deg, ${theme.vars['--clr-p']}, ${theme.vars['--clr-s']})`,
                      }}
                    />
                    <div className="theme-info">
                      <span className="theme-emoji">{theme.emoji}</span>
                      <span className="theme-name">{theme.name}</span>
                    </div>
                    {activeTheme === theme.id && (
                      <span className="theme-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
